import type { Column } from '../../claim-history/simple-table'

import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Fragment, useCallback, useMemo, useReducer } from 'react'
import { useDebounceValue } from 'usehooks-ts'

import http from '../../../lib/http'
import * as Table from '../../claim-history/simple-table'
import { stringifyValue } from '../audit/audit-log'
import { PageHeader } from '../components/page-header'
import { SearchInput } from '../components/search-input'
import styles from '../index.module.css'

interface Entry extends Record<PropertyKey, unknown> {
  id: string
  type: HistoryAction
  key?: string | null
  display_name?: string | null
  group_id?: string | null
  entity?: string | null
  organization?: string | null
  self_enrollment?: boolean | null
  is_active?: boolean | null
  status?: string | null
  previous_values?: Record<string, unknown> | null
  updated_patients?: number | null
  deleted_mobile_users?: string[] | null
  created_at?: string | null
  updated_at?: string | null
  synchronization_id?: string | null
  attributes?: string | null
  client_tags?: string | null
  self_enrollment_display?: string | null
  is_active_display?: string | null
}

const enum HistoryAction {
  Create = 'create',
  Activate = 'activate',
  Update = 'update',
  Terminate = 'terminate',
  UpdatePatients = 'update_patients',
  DeleteMobileUsers = 'delete_mobile_users'
}

let actionMapper: Record<HistoryAction, string> = {
  [HistoryAction.Create]: 'Create',
  [HistoryAction.Update]: 'Update',
  [HistoryAction.Activate]: 'Activate',
  [HistoryAction.Terminate]: 'Terminate',
  [HistoryAction.UpdatePatients]: 'Update Patients',
  [HistoryAction.DeleteMobileUsers]: 'Delete Mobile Users'
}

type QueryKey = [
  'client-history',
  SearchParams
]

interface QueryData {
  data: Entry[]
  total: number
  count: number
  latest?: {
    id: string
    created_at: string
  }
}

interface State {
  searchParams: SearchParams
}

type Action =
  | { type: 'query/set', payload: string }
  | { type: 'filters/set', payload: SearchParams['filters'] }
  | { type: 'filters/merge', payload: Partial<SearchParams['filters']> }
  | { type: 'filters/clear' }
  | { type: 'sort/set', payload: Sort }
  | { type: 'pagination/page/set', payload: number }
  | { type: 'pagination/page-size/set', payload: number }

interface SearchParams {
  query: string

  filters: {
    action: HistoryAction[]
    self_enrollment: boolean[]
    is_active: boolean[]
  }

  sort: Sort
  pagination: Pagination
}

let sortKeys = [
  'created_at'
] as const

type SortKey = typeof sortKeys[number]
type SortDirection = 'asc' | 'desc'

interface Sort {
  field: SortKey
  direction: SortDirection
}

interface Pagination {
  page: number
  pageSize: number
}

let initialState: State = {
  searchParams: {
    query: '',
    filters: {
      action: [],
      self_enrollment: [],
      is_active: []
    },
    sort: {
      field: 'created_at',
      direction: 'desc'
    },
    pagination: {
      page: 0,
      pageSize: 10
    }
  }
}

function reducer (state: State, action: Action): State {
  switch (action.type) {
    case 'query/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          query: action.payload,
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    case 'filters/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          filters: action.payload,
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    case 'filters/merge': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          filters: {
            ...state.searchParams.filters,
            ...action.payload
          },
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    case 'filters/clear': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          query: initialState.searchParams.query,
          filters: initialState.searchParams.filters,
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    case 'sort/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          sort: action.payload,
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    case 'pagination/page/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          pagination: {
            ...state.searchParams.pagination,
            page: action.payload
          }
        }
      }
    }

    case 'pagination/page-size/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          pagination: {
            ...state.searchParams.pagination,
            pageSize: action.payload,
            page: 0
          }
        }
      }
    }

    default: {
      return state
    }
  }
}

let sortMapper: Record<string, string> = {
  patient_name: 'patient_last',
  author_name: 'author_last'
}

function isDefaultSearchParams (searchParams: Pick<SearchParams, 'query' | 'filters'>): boolean {
  return (
    searchParams.query === initialState.searchParams.query &&
    searchParams.filters.action.length === initialState.searchParams.filters.action.length &&
    searchParams.filters.action.every(v => initialState.searchParams.filters.action.includes(v)) &&
    searchParams.filters.self_enrollment.length === initialState.searchParams.filters.self_enrollment.length &&
    searchParams.filters.self_enrollment.every(v => initialState.searchParams.filters.self_enrollment.includes(v)) &&
    searchParams.filters.is_active.length === initialState.searchParams.filters.is_active.length &&
    searchParams.filters.is_active.every(v => initialState.searchParams.filters.is_active.includes(v))
  )
}

export function ClientHistoryPage (): JSX.Element {
  let [ state, dispatch ] = useReducer(reducer, initialState, state => ({
    ...state,
    searchParams: {
      ...state.searchParams,
      filters: {
        ...state.searchParams.filters,
        from: new Date(new Date().getTime() - 7 * 24 * 60 * 60 * 1000).toISOString().substring(0, 10),
        to: new Date().toISOString().substring(0, 10)
      }
    }
  }))

  let [ debouncedSearchQuery ] = useDebounceValue(state.searchParams.query, 500)

  let query = useQuery<unknown, unknown, QueryData, QueryKey>({
    queryKey: [
      'client-history',
      { ...state.searchParams, query: debouncedSearchQuery }
    ],

    async queryFn ({ queryKey }) {
      let [ , searchParams ] = queryKey
      let { query, filters, sort, pagination } = searchParams
      let normalizedQuery = query.trim().toLowerCase()

      let sp = new URLSearchParams([
        [ 'count', pagination.pageSize.toString() ],
        [ 'page', pagination.page.toString() ],
        [ 'on', sortMapper[sort.field] ?? sort.field ],
        [ 'dir', sort.direction ],
        ...filters.action.map(v => [ 'filters.action', v ]),
        ...filters.self_enrollment.map(v => [ 'filters.self_enrollment', v ? 'true' : 'false' ]),
        ...filters.is_active.map(v => [ 'filters.is_active', v ? 'true' : 'false' ]),
        ...(normalizedQuery.length > 0 ? [[ 'search', normalizedQuery ]] : [])
      ])

      let response = await http.getRequest(`/admin/client-history?${sp.toString()}`)
      return response
    },

    placeholderData: keepPreviousData
  })

  let columns = useMemo<Array<Column<Entry>>>(() => [
    {
      name: 'created_at',
      header: 'Time',
      isSortable: true,
      initialSortDirection: 'desc',
      getValue (entry) {
        if (!entry.created_at) {
          return null
        }

        return (
          <span style={{ whiteSpace: 'nowrap' }}>
            {Table.formatTime(entry.created_at)}
          </span>
        )
      }
    },
    {
      name: 'action',
      header: 'Action',
      isSortable: false,
      getValue (entry) {
        if (!entry.type) {
          return null
        }

        return actionMapper[entry.type] ?? entry.action
      }
    },
    {
      name: 'display_name',
      header: 'Name',
      isSortable: false
    },
    {
      name: 'key',
      header: 'Key',
      isSortable: false
    },
    {
      name: 'group_id',
      header: 'Group',
      isSortable: false
    },
    {
      name: 'entity',
      header: 'Entity',
      isSortable: false
    },
    {
      name: 'organization',
      header: 'Organization',
      isSortable: false
    },
    {
      name: 'self_enrollment',
      header: 'Self Enrollment',
      isSortable: false,
      getValue (entry) {
        switch (entry.self_enrollment) {
          case true:
            return 'Allowed'
          case false:
            return 'Forbidden'
          default:
            return null
        }
      }
    },
    {
      name: 'is_active',
      header: 'Compassion Status',
      isSortable: false,
      getValue (entry) {
        switch (entry.is_active) {
          case true:
            return 'Enabled'
          case false:
            return 'Disabled'
          default:
            return null
        }
      }
    },
    {
      name: 'attributes',
      header: 'Attributes',
      isSortable: false
    },
    {
      name: 'client_tags',
      header: 'Compassion View',
      isSortable: false
    },
    {
      name: 'status',
      header: 'CAT Status',
      isSortable: false
    }
  ], [])

  let filters = useMemo<Table.Filter[]>(() => {
    return [
      {
        name: 'action',
        value: state.searchParams.filters.action,
        options: [
          { value: HistoryAction.Create, label: actionMapper[HistoryAction.Create] ?? HistoryAction.Create },
          { value: HistoryAction.Update, label: actionMapper[HistoryAction.Update] ?? HistoryAction.Update },
          { value: HistoryAction.Activate, label: actionMapper[HistoryAction.Activate] ?? HistoryAction.Activate },
          { value: HistoryAction.Terminate, label: actionMapper[HistoryAction.Terminate] ?? HistoryAction.Terminate },
          { value: HistoryAction.UpdatePatients, label: actionMapper[HistoryAction.UpdatePatients] ?? HistoryAction.UpdatePatients },
          { value: HistoryAction.DeleteMobileUsers, label: actionMapper[HistoryAction.DeleteMobileUsers] ?? HistoryAction.DeleteMobileUsers }
        ]
      },
      {
        name: 'self_enrollment',
        value: state.searchParams.filters.self_enrollment,
        options: [
          { value: true, label: 'Allowed' },
          { value: false, label: 'Forbidden' }
        ]
      },
      {
        name: 'is_active',
        value: state.searchParams.filters.is_active,
        options: [
          { value: true, label: 'Enabled' },
          { value: false, label: 'Disabled' }
        ]
      }
    ]
  }, [
    state.searchParams.filters.action,
    state.searchParams.filters.is_active,
    state.searchParams.filters.self_enrollment
  ])

  let handleFilterChange = useCallback(<T extends keyof Entry>(name: T, value: Array<Entry[T]>): void => {
    dispatch({
      type: 'filters/merge',
      payload: {
        [name]: value
      }
    })
  }, [])

  let handleFiltersClear = useCallback((): void => {
    dispatch({
      type: 'filters/clear'
    })
  }, [])

  let handleSortChange = useCallback((field: keyof Entry, direction: SortDirection): void => {
    function isSortKey (key: unknown): key is SortKey {
      return (sortKeys as readonly unknown[]).includes(key)
    }

    if (!isSortKey(field)) {
      return
    }

    dispatch({
      type: 'sort/set',
      payload: {
        field,
        direction
      }
    })
  }, [])

  let handlePageChange = useCallback((value: number): void => {
    dispatch({
      type: 'pagination/page/set',
      payload: value - 1
    })
  }, [])

  let handlePageSizeChange = useCallback((value: number): void => {
    dispatch({
      type: 'pagination/page-size/set',
      payload: value
    })
  }, [])

  let pagination = useMemo(() => {
    return {
      ...state.searchParams.pagination,
      totalFiltered: query.data?.total ?? 0,
      totalUnfiltered: query.data?.total ?? 0,
      extra: (
        <span style={{ color: '#ababab' }}>
          {query.data?.latest && (
            <>
              Last updated at{' '}
              {Table.formatTime(query.data.latest.created_at)}
            </>
          )}

          {!query.data?.latest && (
            <>
              No synchronization history
            </>
          )}
        </span>
      )
    }
  }, [
    state.searchParams.pagination,
    query.data
  ])

  return (
    <div>
      <PageHeader className={styles.pageHeader}>
        <h1>
          <span>Client History</span>
        </h1>

        <SearchInput
          value={state.searchParams.query}
          placeholder='Search by action, client, group, entity, organization, status, attributes, etc.'
          onChange={event => {
            dispatch({
              type: 'query/set',
              payload: event.target.value
            })
          }}
          onFiltersClear={isDefaultSearchParams(state.searchParams) ? undefined : handleFiltersClear}
        />
      </PageHeader>

      <Table.Table
        data={query.data?.data ?? []}
        queryStatus={query.status}
        noDataPlaceholder='No records found'
        forceClearFilters={!isDefaultSearchParams(state.searchParams)}
        columns={columns}
        filters={filters}
        sort={state.searchParams.sort}
        pagination={pagination}
        onFilterChange={handleFilterChange}
        onFiltersClear={handleFiltersClear}
        onSortChange={handleSortChange}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        expandingComponent={(item, state, setState) => {
          let actions: HistoryAction[] = [
            HistoryAction.Update,
            HistoryAction.UpdatePatients,
            HistoryAction.DeleteMobileUsers
          ]

          if (!actions.includes(item.type)) {
            return null
          }

          return (
            <ExpandingComponent item={item} />
          )
        }}
      />
    </div>
  )
}

let keyMapper: Record<string, string> = {
  id: 'ID',
  key: 'Key',
  display_name: 'Name',
  group_id: 'Group',
  entity: 'Entity',
  organization: 'Organization',
  self_enrollment: 'Self Enrollment',
  is_active: 'Compassion Status',
  status: 'CAT Status',
  attributes: 'Attributes',
  client_tags: 'Compassion View'
}

interface ExpandingComponentProps {
  item: Entry
}

function ExpandingComponent ({ item }: ExpandingComponentProps): JSX.Element | null {
  if (item.type === HistoryAction.UpdatePatients) {
    return (
      <div className={styles.expandableRowContent}>
        <div className={styles.row}>
          <div className={styles.details}>
            <span className={styles.label}>Updated Patients</span>
            <span className={styles.value}>
              <div className={styles.modifiedValue}>
                {item.updated_patients ?? 0}
              </div>
            </span>
          </div>
        </div>
      </div>
    )
  }

  if (item.type === HistoryAction.DeleteMobileUsers) {
    return (
      <div className={styles.expandableRowContent}>
        <div
          className={styles.row}
          style={{ gridTemplateColumns: '1fr' }}
        >
          <div className={styles.details}>
            <span className={styles.label}>Deleted Mobile Users</span>
            <span className={styles.value}>
              <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '2rem' }}>
                {item.deleted_mobile_users?.map(email => (
                  <div
                    key={email}
                    className={styles.deletedValue}
                  >
                    {email}
                  </div>
                ))}
              </div>
            </span>
          </div>
        </div>
      </div>
    )
  }

  if (item.type === HistoryAction.Update) {
    return (
      <div className={styles.expandableRowContent}>
        <div className={styles.row}>
          <div className={styles.details}>
            {Object.entries(item.previous_values ?? {}).map(([ key, previousValue ], index) => (
              <Fragment key={index}>
                <span className={styles.label}>{keyMapper[key] ?? key}</span>
                <span className={styles.value}>
                  {(() => {
                    let currentValue = item[key]

                    let stringifiedPreviousValue = stringifyValue(previousValue)
                    let stringifiedCurrentValue = stringifyValue(item[key])

                    if (
                      typeof previousValue !== 'undefined' &&
                      typeof currentValue !== 'undefined' &&
                      (currentValue === null || currentValue === '')
                    ) {
                      return (
                        <span className={styles.deletedValue}>
                          {(stringifiedPreviousValue ?? '') || '(Empty)'}
                        </span>
                      )
                    }

                    if (
                      typeof previousValue !== 'undefined' &&
                      typeof currentValue !== 'undefined'
                    ) {
                      return (
                        <div className={styles.modifiedValue}>
                          <span>
                            {(stringifiedPreviousValue ?? '') || '(Empty)'}
                          </span>

                          <span>
                            {' '}→{' '}
                          </span>

                          <span>
                            {(stringifiedCurrentValue ?? '') || '(Empty)'}
                          </span>
                        </div>
                      )
                    }

                    if (
                      typeof previousValue === 'undefined'
                    ) {
                      return (
                        <span className={styles.addedValue}>
                          {(stringifiedCurrentValue ?? '') || '(Empty)'}
                        </span>
                      )
                    }

                    return null
                  })()}
                </span>
              </Fragment>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return null
}
